import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, UtensilsCrossed, ShoppingBag, Apple, Scissors } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';

function useReveal(ref) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([e]) => {if (e.isIntersecting) setRevealed(true);}, { threshold: 0.15 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return revealed;
}

export default function SectorsSection() {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const revealed = useReveal(ref);

  const sectors = [
    {
      id: 'hosteleria', name: t('sectors.hospitality.name'), icon: UtensilsCrossed,
      headline: t('sectors.hospitality.headline'), description: t('sectors.hospitality.desc'),
      tags: t('sectors.hospitality.tags').split('|'), metric: t('sectors.hospitality.metric'),
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80&auto=format&fit=crop', color: '#0095fc'
    },
    {
      id: 'retail', name: t('sectors.retail.name'), icon: ShoppingBag,
      headline: t('sectors.retail.headline'), description: t('sectors.retail.desc'),
      tags: t('sectors.retail.tags').split('|'), metric: t('sectors.retail.metric'),
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80&auto=format&fit=crop', color: '#7800f7'
    },
    {
      id: 'alimentacion', name: t('sectors.food.name'), icon: Apple,
      headline: t('sectors.food.headline'), description: t('sectors.food.desc'),
      tags: t('sectors.food.tags').split('|'), metric: t('sectors.food.metric'),
      image: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=600&q=80&auto=format&fit=crop', color: '#0ff0b3'
    },
    {
      id: 'belleza', name: t('sectors.beauty.name'), icon: Scissors,
      headline: t('sectors.beauty.headline'), description: t('sectors.beauty.desc'),
      tags: t('sectors.beauty.tags').split('|'), metric: t('sectors.beauty.metric'),
      image: 'https://images.unsplash.com/photo-1634449571010-02389ed0f9b0?w=600&q=80&auto=format&fit=crop', color: '#0095fc'
    }];

  const navigate = useNavigate();
  const goToDemo = () => navigate('/demo');
  const goToPricing = () => navigate('/precios');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sectorId = params.get('sector');
    if (sectorId) {
      const idx = sectors.findIndex((s) => s.id === sectorId);
      if (idx >= 0) setActive(idx);
    }
    const handler = (e) => {
      if (e.detail) {
        const idx = sectors.findIndex((s) => s.id === e.detail);
        if (idx >= 0) setActive(idx);
      }
    };
    window.addEventListener('setActiveSector', handler);
    return () => window.removeEventListener('setActiveSector', handler);
  }, []);

  return (
    <section id="sectores" ref={ref} className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-xs font-black tracking-widest uppercase text-blue-500 mb-4 px-3 py-1 bg-blue-50 rounded-full">{t('sectors.eyebrow')}</span>
          <h2 className="font-heading font-light text-4xl lg:text-5xl text-gray-900 leading-tight mb-4 tracking-tight">
            {t('sectors.title1')} <span className="text-brand-blue">{t('sectors.titleHighlight')}</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto font-light">
            {t('sectors.desc')}
          </p>
        </div>

        {/* Tabs */}
        <div className={`flex flex-wrap justify-center gap-2 mb-12 transition-all duration-700 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {sectors.map((s, i) =>
          <button
            key={s.id}
            onClick={() => setActive(i)}
            aria-pressed={active === i}
            className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 ${
            active === i ?
            'text-white shadow-lg scale-105' :
            'bg-gray-100 text-gray-500 hover:bg-gray-200'}`
            }
            style={active === i ? { background: s.color, boxShadow: `0 8px 24px ${s.color}40` } : {}}>
              <s.icon size={16} style={{ color: active === i ? '#fff' : s.color }} className="shrink-0" />
              <span>{s.name}</span>
            </button>
          )}
        </div>

        {/* Content */}
        <div className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-700 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl order-2 lg:order-1">
            <img
              src={sectors[active].image}
              alt={sectors[active].name}
              loading="lazy"
              decoding="async"
              fetchPriority="high"
              className="w-full h-full object-cover transition-all duration-700" />

            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${sectors[active].color}40 0%, transparent 60%)` }} />
            {/* Metric overlay */}
            <div className="absolute bottom-6 left-6 right-6">
              <div className="glassmorphism rounded-2xl px-5 py-4 border border-white/20">
                <p className="text-white font-black text-lg">{sectors[active].metric}</p>
                <p className="text-white/60 text-xs mt-1">{t('sectors.metricLabel')}</p>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-6 order-1 lg:order-2">
            <div>
              <h3 className="font-heading font-black text-3xl lg:text-4xl text-gray-900 leading-tight mb-4">
                {sectors[active].headline}
              </h3>
              <p className="text-gray-500 text-lg leading-relaxed font-light">
                {sectors[active].description}
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {sectors[active].tags.map((tag) =>
              <span key={tag} className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-full text-sm font-medium border border-gray-100">
                  {tag}
                </span>
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <button
                onClick={goToDemo}
                className="flex items-center gap-2 text-white px-6 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all hover:scale-105 shadow-lg"
                style={{ background: sectors[active].color, boxShadow: `0 8px 20px ${sectors[active].color}40` }}>
                {t('sectors.demo')} <ArrowRight size={16} />
              </button>
              <button
                onClick={goToPricing}
                className="px-6 py-3 rounded-xl font-bold text-sm border-2 text-gray-700 hover:border-gray-400 transition-colors"
                style={{ borderColor: `${sectors[active].color}50` }}>
                {t('sectors.prices')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);
}